import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { useVendorProfile } from '@/hooks/useVendorForUser';
import { useProProfile } from '@/hooks/useProForUser';
import { rolesIncludeAdmin } from '@/lib/auth';
import {
  destinationForIncompleteVendorOnboarding,
  syncSignupNavigationAfterVendorComplete,
} from '@/lib/postSignupNavigation';
import { isJoinPath } from '@/lib/joinPaths';

const ONBOARD_PREFIXES = ['/join', '/vendor/onboard', '/pro/onboard', '/confirm-email'];

function isOnboardingPath(pathname: string): boolean {
  return ONBOARD_PREFIXES.some((p) => pathname.startsWith(p)) || isJoinPath(pathname);
}

/**
 * Sends users who have not finished vendor onboarding back to /join.
 * Does not force pro-only completion — services setup is optional when sell intent is primary.
 */
export function PostSignupRedirect() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { vendor, ready: vendorReady } = useVendorProfile();
  const { pro, ready: proReady } = useProProfile();

  useEffect(() => {
    if (!user || !vendorReady || !proReady) return;
    if (isOnboardingPath(pathname)) return;
    // Admins must not be forced into /join from leftover signup goals.
    if (rolesIncludeAdmin(user.roles)) return;

    if (vendor) {
      syncSignupNavigationAfterVendorComplete(pro);
      return;
    }

    const vendorDest = destinationForIncompleteVendorOnboarding(vendor);
    if (vendorDest && !pathname.startsWith(vendorDest.split('?')[0] ?? vendorDest)) {
      navigate(vendorDest, { replace: true });
    }
  }, [user, vendor, pro, vendorReady, proReady, pathname, navigate]);

  return null;
}
